// Landlord Autopilot — admin-side pages for the new vertical:
//   • Rentals (units + renter + magic-link portal)
//   • Vendors (the 3-person trade book)
//   • Repairs (AI-triaged inbox + one-tap dispatch)
//
// All wrapped in <AdminShell> so the mobile hamburger works on iPhones
// in the driveway. Every interactive element carries a data-testid so
// the testing agent can drive it end-to-end.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Buildings, Plus, Trash, Copy, Link as LinkIcon, ArrowRight, Sparkle, X,
  Wrench, PaperPlaneTilt, CheckCircle, Warning, Phone,
  ClockCounterClockwise, ShieldCheck, House, BellRinging, BellSlash,
} from "@phosphor-icons/react";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

// ═════════════════════════════════════════════════════════════
// RENTALS PAGE
// ═════════════════════════════════════════════════════════════
export function LandlordRentals() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    unit_label: "", address: "", renter_name: "", renter_email: "", renter_phone: "",
    rent_monthly: "", rent_due_day: 1, auto_dispatch_cap: 200, notes_to_renter: "",
  });
  const [busy, setBusy] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", period: "", method: "manual", note: "" });

  const load = async () => {
    try { const r = await api.get("/landlord-auto/rentals"); setItems(r.data.rentals || []); }
    catch (e) { toast.error("Could not load rentals"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.unit_label || !form.address) { toast.error("Unit label + address are required"); return; }
    setBusy(true);
    try {
      await api.post("/landlord-auto/rentals", {
        unit_label: form.unit_label,
        address: form.address,
        renter_name: form.renter_name || null,
        renter_email: form.renter_email || null,
        renter_phone: form.renter_phone || null,
        rent_monthly_cents: Math.round(parseFloat(form.rent_monthly || "0") * 100),
        rent_due_day: parseInt(form.rent_due_day, 10) || 1,
        auto_dispatch_cap_cents: Math.round(parseFloat(form.auto_dispatch_cap || "200") * 100),
        notes_to_renter: form.notes_to_renter || null,
      });
      toast.success("Rental added — magic portal link ready to share");
      setShowForm(false);
      setForm({ unit_label: "", address: "", renter_name: "", renter_email: "", renter_phone: "",
        rent_monthly: "", rent_due_day: 1, auto_dispatch_cap: 200, notes_to_renter: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this rental? The magic portal link will stop working.")) return;
    try { await api.delete(`/landlord-auto/rentals/${id}`); load(); toast.success("Rental removed"); }
    catch { toast.error("Delete failed"); }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Portal link copied — text it to your renter");
    } catch { toast.error("Copy failed — long-press the link"); }
  };

  const rotate = async (id) => {
    if (!window.confirm("Rotate this portal link? The old one will stop working immediately.")) return;
    try {
      const r = await api.post(`/landlord-auto/rentals/${id}/rotate-token`);
      toast.success("New link generated — send it to your renter"); load();
    } catch { toast.error("Rotate failed"); }
  };

  const toggleReminders = async (id, next) => {
    try {
      await api.post(`/landlord-auto/rentals/${id}/reminders/toggle`, { enabled: next });
      toast.success(next ? "Rent reminders ON — texts 3 days before due" : "Rent reminders OFF");
      load();
    } catch { toast.error("Toggle failed"); }
  };
  const sendTestReminder = async (id) => {
    try {
      const r = await api.post(`/landlord-auto/rentals/${id}/reminders/send-test`);
      const st = r.data?.sms_status || r.data?.status;
      if (r.data?.status === "sent" || st === "sent") toast.success("Test reminder sent to renter");
      else if (st === "stubbed") toast.warning("Twilio not wired — SMS logged (configure in Settings › SMS)");
      else toast.info(`Skipped: ${r.data?.reason || "no phone or amount"}`);
    } catch { toast.error("Send test failed"); }
  };

  const recordPayment = async () => {
    if (!payFor) return;
    const cents = Math.round(parseFloat(payForm.amount || "0") * 100);
    if (cents < 100) { toast.error("Enter an amount"); return; }
    if (!payForm.period.match(/^\d{4}-\d{2}$/)) { toast.error("Period should be YYYY-MM"); return; }
    try {
      await api.post("/landlord-auto/rent-payments", {
        rental_id: payFor.id,
        amount_cents: cents,
        period: payForm.period,
        method: payForm.method,
        note: payForm.note || null,
      });
      toast.success("Payment recorded");
      setPayFor(null); setPayForm({ amount: "", period: "", method: "manual", note: "" });
      load();
    } catch (e) { toast.error("Record failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="landlord-rentals-page">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-600 text-white flex items-center justify-center">
              <Buildings weight="fill" size={22}/>
            </div>
            <div>
              <h1 className="font-display font-black text-3xl leading-none">Rentals</h1>
              <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
                One rental = one magic portal link · Auto-dispatch cap set per unit
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/landlord/vendors" data-testid="rentals-vendors-link"
              className="h-11 px-3 bg-white hover:bg-slate-50 border-2 border-slate-200 text-slate-800 font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-1">
              <Wrench weight="fill" size={14}/> Vendors
            </Link>
            <Link to="/admin/landlord/repairs" data-testid="rentals-repairs-link"
              className="h-11 px-3 bg-white hover:bg-slate-50 border-2 border-slate-200 text-slate-800 font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-1">
              <Sparkle weight="fill" size={14}/> Repairs
            </Link>
            <button onClick={() => setShowForm(v => !v)} data-testid="rentals-add-btn"
              className="h-11 px-5 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
              <Plus weight="bold" size={16}/> {showForm ? "Cancel" : "Add Rental"}
            </button>
          </div>
        </div>

        {showForm && (
          <div className="bg-white border-2 border-sky-500 rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="rentals-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Unit label *"><input value={form.unit_label} onChange={e => setForm({...form, unit_label: e.target.value})} placeholder="Unit 8A" data-testid="rental-unit-label" className={inputCls}/></Field>
              <Field label="Address *"><input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="5346 Chew Ave, Philadelphia PA 19144" data-testid="rental-address" className={inputCls}/></Field>
              <Field label="Renter name"><input value={form.renter_name} onChange={e => setForm({...form, renter_name: e.target.value})} placeholder="Jane Renter" data-testid="rental-renter-name" className={inputCls}/></Field>
              <Field label="Renter phone"><input value={form.renter_phone} onChange={e => setForm({...form, renter_phone: e.target.value})} placeholder="215-555-0100" data-testid="rental-renter-phone" className={inputCls}/></Field>
              <Field label="Renter email"><input value={form.renter_email} onChange={e => setForm({...form, renter_email: e.target.value})} placeholder="jane@email.com" data-testid="rental-renter-email" className={inputCls}/></Field>
              <Field label="Monthly rent ($)"><input type="number" min="0" step="1" value={form.rent_monthly} onChange={e => setForm({...form, rent_monthly: e.target.value})} placeholder="1350" data-testid="rental-rent" className={inputCls}/></Field>
              <Field label="Rent due on (day)"><input type="number" min="1" max="28" value={form.rent_due_day} onChange={e => setForm({...form, rent_due_day: e.target.value})} data-testid="rental-due-day" className={inputCls}/></Field>
              <Field label="Auto-dispatch cap ($)"><input type="number" min="0" step="10" value={form.auto_dispatch_cap} onChange={e => setForm({...form, auto_dispatch_cap: e.target.value})} data-testid="rental-cap" className={inputCls}/></Field>
              <div className="md:col-span-2"><Field label="Note the renter sees on their portal"><textarea rows={2} value={form.notes_to_renter} onChange={e => setForm({...form, notes_to_renter: e.target.value})} placeholder="Text me for emergencies at 215-555-0142. Non-emergencies — use the button below." data-testid="rental-notes" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg"/></Field></div>
            </div>
            <button onClick={save} disabled={busy} data-testid="rentals-save-btn" className="mt-4 h-11 px-6 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
              {busy ? "Saving…" : "Save & Generate Portal Link"}
            </button>
          </div>
        )}

        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="rentals-empty">
            <Buildings weight="fill" size={40} className="text-slate-300 mx-auto mb-2"/>
            <div className="font-bold text-slate-500 mb-1">No rentals yet</div>
            <div className="text-xs">Add your first unit — you'll get a magic portal link to text your renter in 10 seconds.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(r => (
              <div key={r.id} data-testid={`rental-row-${r.id}`}
                className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-lg">{r.unit_label}</span>
                      {r.open_repairs > 0 && (
                        <span className="text-[10px] font-black uppercase tracking-widest bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" data-testid={`rental-open-repairs-${r.id}`}>
                          {r.open_repairs} open repair{r.open_repairs === 1 ? "" : "s"}
                        </span>
                      )}
                      {r.last_paid_at && (
                        <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                          Last paid {r.last_paid_at.slice(0, 10)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">{r.address}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Renter: <span className="font-bold">{r.renter_name || "(unset)"}</span>
                      {r.renter_phone && <> · {r.renter_phone}</>}
                      {" · "}Rent: <span className="font-bold">{dollars(r.rent_monthly_cents)}</span> due day {r.rent_due_day}
                      {" · "}Cap: <span className="font-mono">{dollars(r.auto_dispatch_cap_cents)}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-[200px] px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-lg font-mono text-[11px] text-sky-800 truncate" title={r.portal_url} data-testid={`rental-portal-url-${r.id}`}>
                    {r.portal_url}
                  </div>
                  <button onClick={() => copyLink(r.portal_url)} data-testid={`rental-copy-${r.id}`}
                    className="h-9 px-3 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <Copy weight="fill" size={12}/> Copy
                  </button>
                  <a href={r.portal_url} target="_blank" rel="noreferrer" data-testid={`rental-open-${r.id}`}
                    className="h-9 px-3 bg-white hover:bg-slate-100 border-2 border-slate-200 text-slate-700 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <LinkIcon weight="fill" size={12}/> Open
                  </a>
                  <button onClick={() => rotate(r.id)} data-testid={`rental-rotate-${r.id}`}
                    className="h-9 px-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <ClockCounterClockwise weight="fill" size={12}/> Rotate
                  </button>
                  <button onClick={() => { setPayFor(r); const now = new Date(); setPayForm({ amount: (r.rent_monthly_cents / 100).toFixed(0), period: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`, method: "manual", note: "" }); }}
                    data-testid={`rental-log-payment-${r.id}`}
                    className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <CheckCircle weight="fill" size={12}/> Log Payment
                  </button>
                  {/* Rent Reminders — toggle + test-send. Reminder fires 3d before due day */}
                  <button onClick={() => toggleReminders(r.id, !(r.rent_reminder_enabled === false ? false : true))}
                    aria-label={r.rent_reminder_enabled === false ? "Turn rent reminders on" : "Turn rent reminders off"}
                    data-testid={`rental-reminder-toggle-${r.id}`}
                    className={`h-9 px-3 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 ${
                      r.rent_reminder_enabled === false
                        ? "bg-slate-100 hover:bg-slate-200 text-slate-500"
                        : "bg-amber-500 hover:bg-amber-600 text-white"
                    }`}>
                    {r.rent_reminder_enabled === false
                      ? (<><BellSlash weight="fill" size={12}/> Reminders Off</>)
                      : (<><BellRinging weight="fill" size={12}/> Reminders On</>)}
                  </button>
                  {r.rent_reminder_enabled !== false && r.renter_phone && r.rent_monthly_cents > 0 && (
                    <button onClick={() => sendTestReminder(r.id)}
                      aria-label={`Send a test rent reminder to ${r.renter_name || "renter"} now`}
                      data-testid={`rental-reminder-test-${r.id}`}
                      className="h-9 px-3 bg-white hover:bg-amber-50 border-2 border-amber-300 text-amber-800 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                      <PaperPlaneTilt weight="fill" size={12}/> Test
                    </button>
                  )}
                  <Link to={`/admin/landlord/rentals/${r.id}/compliance`} data-testid={`rental-compliance-${r.id}`}
                    className="h-9 px-3 bg-white hover:bg-slate-50 border-2 border-slate-200 text-slate-800 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <ShieldCheck weight="fill" size={12}/> Compliance
                  </Link>
                  <Link to={`/admin/landlord/rentals/${r.id}/applications`} data-testid={`rental-applications-${r.id}`}
                    className="h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    Applications
                  </Link>
                  <Link to={`/admin/landlord/rentals/${r.id}/intake`} data-testid={`rental-intake-${r.id}`}
                    className="h-9 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <Wrench weight="fill" size={12}/> Onboard
                  </Link>
                  <Link to={`/admin/landlord/rentals/${r.id}/lease`} data-testid={`rental-lease-${r.id}`}
                    className="h-9 px-3 bg-white hover:bg-slate-50 border-2 border-slate-200 text-slate-800 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    Lease
                  </Link>
                  <button onClick={() => remove(r.id)} data-testid={`rental-remove-${r.id}`}
                    aria-label={`Remove rental ${r.unit_label}`}
                    className="h-9 w-9 rounded-lg bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center">
                    <Trash size={14}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Log payment modal */}
        {payFor && (
          <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setPayFor(null)} data-testid="log-payment-modal">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <div className="font-display font-black text-xl">Log Rent Payment</div>
                <button onClick={() => setPayFor(null)} className="text-slate-400 hover:text-slate-800"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-3">
                <div className="text-sm text-slate-600">{payFor.unit_label} — {payFor.renter_name || "renter"}</div>
                <Field label="Amount ($)"><input type="number" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})} data-testid="log-payment-amount" className={inputCls}/></Field>
                <Field label="Period (YYYY-MM)"><input value={payForm.period} onChange={e => setPayForm({...payForm, period: e.target.value})} placeholder="2026-02" data-testid="log-payment-period" className={inputCls}/></Field>
                <Field label="Method">
                  <select value={payForm.method} onChange={e => setPayForm({...payForm, method: e.target.value})} data-testid="log-payment-method" className={inputCls}>
                    <option value="manual">Manual entry</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                    <option value="zelle">Zelle</option>
                    <option value="venmo">Venmo</option>
                    <option value="ach">ACH (outside Stripe)</option>
                  </select>
                </Field>
                <Field label="Note (optional)"><input value={payForm.note} onChange={e => setPayForm({...payForm, note: e.target.value})} data-testid="log-payment-note" className={inputCls}/></Field>
                <button onClick={recordPayment} data-testid="log-payment-save"
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 shadow">
                  <CheckCircle weight="fill" size={14}/> Record Payment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

// ═════════════════════════════════════════════════════════════
// VENDORS PAGE
// ═════════════════════════════════════════════════════════════
export function LandlordVendors() {
  const [items, setItems] = useState([]);
  const [trades, setTrades] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", trades: [], notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [v, t] = await Promise.all([api.get("/landlord-auto/vendors"), api.get("/landlord-auto/vendors/trades")]);
      setItems(v.data.vendors || []); setTrades(t.data.trades || []);
    } catch { toast.error("Could not load vendors"); }
  };
  useEffect(() => { load(); }, []);

  const toggleTrade = (t) => {
    setForm(f => ({ ...f, trades: f.trades.includes(t) ? f.trades.filter(x => x !== t) : [...f.trades, t] }));
  };

  const save = async () => {
    if (!form.name || !form.phone) { toast.error("Name + phone required"); return; }
    setBusy(true);
    try {
      await api.post("/landlord-auto/vendors", form);
      toast.success("Vendor added");
      setShowForm(false); setForm({ name: "", phone: "", trades: [], notes: "" });
      load();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vendor from your book?")) return;
    try { await api.delete(`/landlord-auto/vendors/${id}`); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="landlord-vendors-page">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
              <Wrench weight="fill" size={22}/>
            </div>
            <div>
              <h1 className="font-display font-black text-3xl leading-none">Vendor Book</h1>
              <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
                Your 3 trusted trades · AI picks the right one for every repair
              </div>
            </div>
          </div>
          <button onClick={() => setShowForm(v => !v)} data-testid="vendors-add-btn"
            className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Plus weight="bold" size={16}/> {showForm ? "Cancel" : "Add Vendor"}
          </button>
        </div>

        {showForm && (
          <div className="bg-white border-2 border-emerald-500 rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="vendors-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name *"><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Mike's Plumbing" data-testid="vendor-name" className={inputCls}/></Field>
              <Field label="Phone *"><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="215-555-0142" data-testid="vendor-phone" className={inputCls}/></Field>
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1.5">Trades</div>
              <div className="flex flex-wrap gap-1.5" data-testid="vendor-trade-chips">
                {trades.map(t => (
                  <button key={t} type="button" onClick={() => toggleTrade(t)}
                    data-testid={`vendor-trade-${t}`}
                    className={`h-8 px-3 rounded-lg text-[11px] font-black uppercase tracking-widest border-2 transition ${
                      form.trades.includes(t) ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-500 hover:border-emerald-400"
                    }`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3"><Field label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="vendor-notes" className="w-full px-3 py-2 border-2 border-slate-200 focus:border-emerald-500 outline-none rounded-lg"/></Field></div>
            <button onClick={save} disabled={busy} data-testid="vendors-save-btn" className="mt-4 h-11 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
              {busy ? "Saving…" : "Add to Vendor Book"}
            </button>
          </div>
        )}

        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="vendors-empty">
            <Wrench weight="fill" size={40} className="text-slate-300 mx-auto mb-2"/>
            <div className="font-bold text-slate-500 mb-1">No vendors yet</div>
            <div className="text-xs">Add your plumber, electrician, and HVAC guy — AI will match the right one to every repair.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map(v => (
              <div key={v.id} data-testid={`vendor-row-${v.id}`} className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-lg">{v.name}</div>
                    <a href={`tel:${v.phone}`} className="text-sm text-emerald-700 font-bold inline-flex items-center gap-1 mt-0.5"><Phone weight="fill" size={12}/> {v.phone}</a>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(v.trades || []).map(t => (
                        <span key={t} className="text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                    {v.notes && <div className="text-xs text-slate-500 mt-2">{v.notes}</div>}
                  </div>
                  <button onClick={() => remove(v.id)} data-testid={`vendor-remove-${v.id}`}
                    aria-label={`Remove ${v.name} from vendor book`}
                    className="h-9 w-9 rounded-lg bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center">
                    <Trash size={14}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}

// ═════════════════════════════════════════════════════════════
// REPAIRS INBOX (triage + dispatch)
// ═════════════════════════════════════════════════════════════
const SEVERITY_META = {
  emergency: { color: "bg-red-500 text-white", label: "EMERGENCY" },
  urgent:    { color: "bg-orange-500 text-white", label: "URGENT" },
  standard:  { color: "bg-sky-500 text-white", label: "STANDARD" },
  cosmetic:  { color: "bg-slate-400 text-white", label: "COSMETIC" },
};
const STATUS_META = {
  new:         { color: "bg-fuchsia-100 text-fuchsia-700", label: "NEW" },
  triaged:     { color: "bg-sky-100 text-sky-700", label: "TRIAGED" },
  dispatched:  { color: "bg-emerald-100 text-emerald-700", label: "DISPATCHED" },
  in_progress: { color: "bg-amber-100 text-amber-700", label: "IN PROGRESS" },
  resolved:    { color: "bg-slate-200 text-slate-600", label: "RESOLVED" },
  cancelled:   { color: "bg-slate-100 text-slate-500", label: "CANCELLED" },
};

export function LandlordRepairs() {
  const [data, setData] = useState({ repairs: [], counts: {}, statuses: [], severities: [] });
  const [vendors, setVendors] = useState([]);
  const [triaging, setTriaging] = useState(null);   // repair being triaged (spinner)
  const [dispatching, setDispatching] = useState(null);
  const [dispatchOverride, setDispatchOverride] = useState({}); // { repairId: { vendor_id, message } }

  const load = async () => {
    try {
      const [r, v] = await Promise.all([api.get("/landlord-auto/repairs"), api.get("/landlord-auto/vendors")]);
      setData(r.data); setVendors(v.data.vendors || []);
    } catch { toast.error("Could not load repairs"); }
  };
  useEffect(() => { load(); }, []);

  const triage = async (id) => {
    setTriaging(id);
    try {
      const r = await api.post(`/landlord-auto/repairs/${id}/triage`);
      const t = r.data.triage;
      toast.success(`${t.severity?.toUpperCase()} · ${t.category} · ${t.suggested_vendor_name || "no vendor match"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Triage failed")); }
    finally { setTriaging(null); }
  };

  const dispatch = async (id, autoBody) => {
    setDispatching(id);
    try {
      const override = dispatchOverride[id] || {};
      const r = await api.post(`/landlord-auto/repairs/${id}/dispatch`, {
        vendor_id: override.vendor_id || null,
        message: override.message || null,
      });
      const st = r.data.dispatch?.sms_status;
      if (st === "sent") toast.success("Vendor texted");
      else if (st === "stubbed") toast.warning("Twilio not wired — SMS logged (configure in Settings › SMS to actually send)");
      else toast.error("SMS failed to send");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dispatch failed")); }
    finally { setDispatching(null); }
  };

  const setStatus = async (id, status) => {
    try { await api.post(`/landlord-auto/repairs/${id}/status`, { status }); load(); }
    catch { toast.error("Status update failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6" data-testid="landlord-repairs-page">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-fuchsia-600 text-white flex items-center justify-center">
              <Sparkle weight="fill" size={22}/>
            </div>
            <div>
              <h1 className="font-display font-black text-3xl leading-none">Repair Inbox</h1>
              <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
                AI triages · You approve · Vendor gets texted
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/landlord/rentals" data-testid="repairs-rentals-link"
              className="h-11 px-3 bg-white hover:bg-slate-50 border-2 border-slate-200 text-slate-800 font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-1">
              <Buildings weight="fill" size={14}/> Rentals
            </Link>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5" data-testid="repairs-counts">
          {["new","triaged","dispatched","in_progress","resolved","cancelled"].map(k => {
            const m = STATUS_META[k];
            return (
              <div key={k} className={`p-3 rounded-xl ${m.color} text-center`} data-testid={`repairs-count-${k}`}>
                <div className="text-2xl font-display font-black">{data.counts?.[k] ?? 0}</div>
                <div className="text-[10px] font-black uppercase tracking-widest">{m.label}</div>
              </div>
            );
          })}
        </div>

        {data.repairs.length === 0 ? (
          <div className="p-8 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl" data-testid="repairs-empty">
            <Wrench weight="fill" size={40} className="text-slate-300 mx-auto mb-2"/>
            <div className="font-bold text-slate-500 mb-1">No repair requests yet</div>
            <div className="text-xs">When your renter submits a repair via their portal, it lands here.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {data.repairs.map(r => {
              const t = r.triage || {};
              const d = r.dispatch;
              const sev = t.severity && SEVERITY_META[t.severity];
              const st = STATUS_META[r.status] || STATUS_META.new;
              const canDispatch = r.status === "triaged" && (t.suggested_vendor_id || vendors.length > 0);
              return (
                <div key={r.id} data-testid={`repair-row-${r.id}`}
                  className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${st.color}`}>{st.label}</span>
                        {sev && <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${sev.color}`}>{sev.label}</span>}
                        {t.category && <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-1 rounded">{t.category}</span>}
                        <span className="font-bold text-lg">{r.title}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {r.rental_label || "(rental)"} · from {r.submitter_name || "renter"} · {r.created_at?.slice(0, 16).replace("T", " ")}
                      </div>
                      <div className="text-sm text-slate-700 mt-2">{r.description}</div>

                      {/* Triage panel */}
                      {t.reasoning && (
                        <div className="mt-3 p-3 rounded-lg bg-sky-50 border-l-4 border-sky-500" data-testid={`repair-triage-${r.id}`}>
                          <div className="text-[10px] uppercase font-black tracking-widest text-sky-700 mb-1">AI Triage</div>
                          <div className="text-sm text-slate-800">{t.reasoning}</div>
                          {t.renter_advice && (
                            <div className="mt-2 text-xs text-slate-700"><b>Told renter:</b> {t.renter_advice}</div>
                          )}
                          <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                            {t.est_cost_low_cents !== undefined && (
                              <span className="font-mono bg-white border border-sky-200 rounded px-2 py-0.5">
                                Est. {dollars(t.est_cost_low_cents)}–{dollars(t.est_cost_high_cents)}
                              </span>
                            )}
                            {t.suggested_vendor_name && (
                              <span className="font-mono bg-emerald-100 text-emerald-800 rounded px-2 py-0.5">
                                Match: {t.suggested_vendor_name}
                              </span>
                            )}
                            {t.auto_dispatch_eligible ? (
                              <span className="font-black uppercase tracking-widest bg-emerald-600 text-white rounded px-2 py-0.5">
                                Under ${(t.auto_dispatch_cap_cents / 100).toFixed(0)} cap · Auto-dispatch OK
                              </span>
                            ) : (t.severity === "emergency" ? (
                              <span className="font-black uppercase tracking-widest bg-red-600 text-white rounded px-2 py-0.5">
                                Emergency · Never auto-dispatched
                              </span>
                            ) : (
                              <span className="font-black uppercase tracking-widest bg-amber-500 text-white rounded px-2 py-0.5">
                                Over cap · Your approval required
                              </span>
                            ))}
                          </div>
                          {t.drafted_sms && (
                            <div className="mt-3">
                              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Drafted SMS</div>
                              <textarea rows={2}
                                data-testid={`repair-draft-sms-${r.id}`}
                                defaultValue={t.drafted_sms}
                                onChange={e => setDispatchOverride(p => ({ ...p, [r.id]: { ...(p[r.id] || {}), message: e.target.value } }))}
                                className="w-full px-2 py-1.5 text-sm border-2 border-slate-200 rounded-lg font-mono"/>
                              {vendors.length > 0 && (
                                <select
                                  data-testid={`repair-vendor-select-${r.id}`}
                                  defaultValue={t.suggested_vendor_id || ""}
                                  onChange={e => setDispatchOverride(p => ({ ...p, [r.id]: { ...(p[r.id] || {}), vendor_id: e.target.value } }))}
                                  className="mt-1.5 h-9 px-2 border-2 border-slate-200 rounded-lg text-sm w-full">
                                  <option value="">— pick vendor —</option>
                                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name} ({v.trades.join(", ") || "any"})</option>)}
                                </select>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Dispatch panel */}
                      {d && (
                        <div className="mt-3 p-3 rounded-lg bg-emerald-50 border-l-4 border-emerald-500" data-testid={`repair-dispatch-${r.id}`}>
                          <div className="text-[10px] uppercase font-black tracking-widest text-emerald-700 mb-1">
                            {d.sms_status === "sent" ? "SMS Sent" : d.sms_status === "stubbed" ? "SMS Stubbed (Twilio not configured)" : "SMS Failed"}
                          </div>
                          <div className="text-sm"><b>{d.vendor_name}</b> · {d.vendor_phone}</div>
                          <div className="text-xs text-slate-600 mt-1 font-mono">{d.sms_body}</div>
                          {d.sms_note && <div className="text-[11px] text-amber-700 mt-1">{d.sms_note}</div>}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-1.5 items-stretch flex-shrink-0 min-w-[160px]">
                      {r.status === "new" && (
                        <button onClick={() => triage(r.id)} disabled={triaging === r.id}
                          data-testid={`repair-triage-btn-${r.id}`}
                          className="h-9 px-3 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1 shadow disabled:opacity-50">
                          <Sparkle weight="fill" size={12}/> {triaging === r.id ? "Triaging…" : "AI Triage"}
                        </button>
                      )}
                      {canDispatch && (
                        <button onClick={() => dispatch(r.id)} disabled={dispatching === r.id}
                          data-testid={`repair-dispatch-btn-${r.id}`}
                          className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1 shadow disabled:opacity-50">
                          <PaperPlaneTilt weight="fill" size={12}/> {dispatching === r.id ? "Sending…" : "Dispatch Vendor"}
                        </button>
                      )}
                      {r.status === "dispatched" && (
                        <button onClick={() => setStatus(r.id, "in_progress")} data-testid={`repair-inprogress-btn-${r.id}`}
                          className="h-9 px-3 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1">
                          <ClockCounterClockwise weight="fill" size={12}/> Mark In Progress
                        </button>
                      )}
                      {(r.status === "dispatched" || r.status === "in_progress") && (
                        <button onClick={() => setStatus(r.id, "resolved")} data-testid={`repair-resolve-btn-${r.id}`}
                          className="h-9 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center justify-center gap-1">
                          <CheckCircle weight="fill" size={12}/> Mark Resolved
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
